import React from 'react';
import {Alert, AlertTitle, Box, Button, Card, CardContent, Collapse, Stack, Typography} from '@mui/material';

type ErrorBoundaryProps = {
  children: React.ReactNode;
  /**
   * Optional: force dev-like detailed error UI (useful for staging).
   * Default: process.env.NODE_ENV !== 'production'
   */
  showDetails?: boolean;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
};

class PWMErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {hasError: false};

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    const errorState = {hasError: true, error};
    console.dir(errorState);
    return errorState;
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({errorInfo, hasError: true, error: error || new Error('Unknown error')});
    console.log('error caught in tree')
    // Keep it simple: log for developers; wire up telemetry here if desired.
    // eslint-disable-next-line no-console
    console.error('Unhandled UI error:', error, errorInfo);
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const showDetails =
      typeof this.props.showDetails === 'boolean'
        ? this.props.showDetails
        : process.env.NODE_ENV !== 'production';

    return (
      <Card variant="outlined" sx={{mx: 2, my: 2}}>
        <CardContent>
          <Stack spacing={2}>
            {showDetails ? (
              <>
                <Alert severity="error">
                  <AlertTitle>UI Error (development)</AlertTitle>
                  The app hit an unhandled rendering error. Details are below.
                </Alert>

                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Message
                  </Typography>
                  <Typography variant="body2" sx={{fontFamily: 'monospace', whiteSpace: 'pre-wrap'}}>
                    {this.state.error?.message || '(no message)'}
                  </Typography>
                </Box>

                <Collapse in={Boolean(this.state.error?.stack)}>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Stack
                    </Typography>
                    <Typography variant="body2" sx={{fontFamily: 'monospace', whiteSpace: 'pre-wrap'}}>
                      {this.state.error?.stack || ''}
                    </Typography>
                  </Box>
                </Collapse>

                <Collapse in={Boolean(this.state.errorInfo?.componentStack)}>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Component stack
                    </Typography>
                    <Typography variant="body2" sx={{fontFamily: 'monospace', whiteSpace: 'pre-wrap'}}>
                      {this.state.errorInfo?.componentStack || ''}
                    </Typography>
                  </Box>
                </Collapse>

                <Stack direction="row" spacing={1}>
                  <Button variant="contained" onClick={this.handleReload}>
                    Reload
                  </Button>
                </Stack>
              </>
            ) : (
              <>
                <Alert severity="error">
                  <AlertTitle>Something went wrong</AlertTitle>
                  Please try reloading the page. If the problem persists, contact support.
                </Alert>

                <Stack direction="row" spacing={1}>
                  <Button variant="contained" onClick={this.handleReload}>
                    Reload
                  </Button>
                </Stack>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  }
}
export default PWMErrorBoundary;